import { combineLatest, Observable, of, ReplaySubject, switchMap } from 'rxjs';

import { ChangeDetectionStrategy, Component } from '@angular/core';

import { NzModalRef } from 'ng-zorro-antd/modal';

import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';

import { params } from '@app/configurations';
import { AccountGroupApiService } from '@app/core/api/account-group-api.service';
import { ApiAccessLevel } from '@app/core/models/api.models';
import { Customer } from '@app/core/models/customer.models';
import { IntercomService } from '@app/core/services/intercom.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { GoTo } from '@app/core/store/states/router-nav/router-nav-state.actions';
import { RouterPath } from '@app/core/store/states/router-nav/router-nav-state.model';
import { UserState } from '@app/core/store/states/user/user.state';
import { isCustomerFull } from '@app/core/utilities/customer.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'adspt-account-limit-dialog',
  templateUrl: './account-limit-dialog.component.html',
  styleUrls: ['./account-limit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountLimitDialogComponent {
  readonly isTrial$: Observable<boolean> = this.store.select(CustomerState.isTrial);

  readonly isEssentialSegment$: Observable<boolean> = this.store.select(CustomerState.isEssentialSegment);

  readonly isAgency$: Observable<boolean> = combineLatest([
    this.store.select(CustomerState.isAgencyAdmin),
    this.store.select(CustomerState.isAgencyClient),
    this.store.select(UserState.isSysAdmin),
  ]).pipe(map(([isAgencyAdmin, isAgencyClient, isSysAdmin]) => isAgencyAdmin || (isAgencyClient && isSysAdmin)));

  readonly loading$ = new ReplaySubject<boolean>(1);

  readonly accountGroups$: Observable<(Customer & { full: boolean })[]> = this.store
    .select(CustomerState.customer)
    .pipe(
      map((customer) => customer?.mainCustomerId || customer?.customerId),
      distinctUntilChanged(),
      tap(() => this.loading$.next(true)),
      switchMap((mainCustomerId) =>
        this.ag.getAll<Customer>({
          filterValues: { mainCustomerId },
          sortValues: { name: 1 },
          include: ['customerId', 'name', 'accountsCount', 'segment', 'trial'],
          accessLevel: ApiAccessLevel.AgencyAdmin,
        }),
      ),
      map((accountGroups) =>
        accountGroups.map((accountGroup) => ({
          ...accountGroup,
          full: !isCustomerFull(accountGroup),
        })),
      ),
      catchError(() => of([])),
      tap(() => this.loading$.next(false)),
    );

  readonly trackByAccountGroup = (_, accountGroup: Customer & { full: boolean }) => accountGroup.customerId;

  constructor(
    readonly modalRef: NzModalRef,
    private ic: IntercomService,
    private ts: TranslateService,
    private ag: AccountGroupApiService,
    private store: Store,
  ) {}

  onOk(createAccountGroup?: boolean): void {
    if (createAccountGroup) {
      if (this.store.selectSnapshot(CustomerState.isAgencyClient)) {
        const customerId = this.store.selectSnapshot(CustomerState.customer).mainCustomerId;
        window.open(`/settings/agency/account-groups?${params.CUSTOMER_ID}=${customerId}&create=true`, '_blank');
      } else {
        this.store.dispatch(new GoTo(RouterPath.AgencyAccountGroupSettings, { queryParams: { create: true } }));
      }
    } else {
      const translations = this.ts.instant('common.account-limit');
      const isTrial = lastValue(this.isTrial$);

      this.ic.showNewMessage(isTrial ? translations['support-trial'] : translations['support-subscription']);
    }
    this.modalRef.close();
  }

  onAccountGroupSelect(accountGroup: Customer): void {
    window.open(`/signup/connect-account?${params.CUSTOMER_ID}=${accountGroup.customerId}`, '_blank');
    this.modalRef.close();
  }
}
